import { PAGE_BG_STYLE } from "@/lib/pageBg";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { Sparkles, TrendingUp, Coins, Target, Rocket, Trophy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { PageHero } from "@/components/PageHero";

export default function NftInfo() {
  return (
    <div className="min-h-screen relative text-foreground flex flex-col" style={PAGE_BG_STYLE}>
      <AppHeader currentPage="nft-info" />
      
      <main className="flex-1 pt-20 pb-24 px-4">
        <div className="max-w-7xl mx-auto space-y-4">
          <PageHero
            title="Understanding NFTs"
            subtitle="Discover the new frontier of digital asset earnings and collecting"
          />

          {/* Content cards */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-primary/10 via-card to-card border-primary/20 hover:border-primary/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Sparkles className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">What is an NFT?</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      NFT stands for <strong className="text-foreground">Non-Fungible Token</strong>. It's a digital item—an image, video, or GIF—that an author decides to save, and it cannot be replicated. Every NFT is therefore a unique piece in the world.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-accent/10 via-card to-card border-accent/20 hover:border-accent/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <TrendingUp className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Why is it the new frontier for earning?</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      The NFT market started in 2017 and has been continuously growing and evolving. The highest-priced NFT ever sold reached <strong className="text-foreground">$69 million</strong>. Imagine the foresight of the person who acquired it, maybe for just a few dollars, and then received that offer—how would you have reacted if it had been you?
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-primary/10 via-card to-card border-primary/20 hover:border-primary/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Coins className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">How does it work?</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Periodically, there are <strong className="text-foreground">MINTs</strong>, some in a lottery format, others on a first-come, first-served basis. They work very simply. Collections can be anything: an image of LeBron James dunking, sharks dressed as humans, or works of art. Each MINT has a limited number of pieces—2,000, 4,000, or so. Every collection is different, and each MINT has a different number of participants, ranging from 20,000 to 250,000.
                    </p>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Everyone participates equally, with a fixed purchase price, and pieces are randomly assigned to some participants. For example, if 100,000 people participate and there are 5,000 pieces, each of these 5,000 pieces will go to 5,000 participants, while the remaining 95,000 will be refunded.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Each MINT has a different price, maybe <strong className="text-foreground">$100 or $300</strong>, and money is used <strong className="text-foreground">only if you actually win the NFT</strong>.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-accent/10 via-card to-card border-accent/20 hover:border-accent/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Trophy className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Rarity & Value</h2>
                    <p className="text-muted-foreground leading-relaxed">
                      Some pieces will be rarer and more valuable than others. This variation in rarity creates opportunities for collectors and investors to discover hidden gems within each collection.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-primary/10 via-card to-card border-primary/20 hover:border-primary/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Target className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Once you get the piece</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      Once you secure an NFT, the "game" becomes interesting. You can choose to keep it in your collection—for example, as a fan of LeBron James, or because you believe its value will grow 30x in the future.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Alternatively, you can list it on the <strong className="text-foreground">NFT marketplace</strong>, where people who missed the MINT—or others who didn't participate—can make offers. Often, these offers can be very high, reflecting the uniqueness of your piece. This happens in 9 out of 10 cases.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-accent/10 via-card to-card border-accent/20 hover:border-accent/40 transition-all duration-300 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
                  <div className="p-3 rounded-xl gradient-primary flex-shrink-0">
                    <Rocket className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground mb-2">Why it's worth joining today</h2>
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      The NFT market has been steadily growing worldwide since 2017. More people are joining all the time. For example, Ronaldo announced the release of his NFT collection on Instagram on September 24. The growth trajectory could be similar to Bitcoin: remember those who bought it in 2008 for $20 and are now millionaires? The process could follow the same path.
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      Even today, especially internationally, NFTs are highly sought after. People are willing to pay <strong className="text-foreground">$30,000–$40,000</strong> for an NFT originally purchased for <strong className="text-foreground">$500</strong>, seeing the long-term potential and the opportunity to secure unique digital assets early.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
